// Modelo de leitura sobre o payload do backup. Tudo puro: recebe as tabelas, devolve
// o que a tela precisa. Regime de CAIXA (período pela data do lançamento) — o app tem
// o regime de competência pra cartão; quando a tela crescer, importar de lá.
import type { AccountRow, BackupPayload, BudgetRow, CategoryRow, CreditCardRow, InvoiceRow, RecurringRow, SubcategoryRow,
  TagRow, TransactionRow, ReservaRow, ReservaMovementRow, GoalRow, GoalDepositRow, PersonRow, PersonalLoanRow, PersonalLoanEventRow, FinancingRow, FinancingAdjustmentRow } from './types';
import { displayMonth, withCharges } from './cards';
import { addMonths, todayMonth } from './format';
import { inPeriod, type Period } from './period';
import { materializedKeys, projectRecurring } from './recurring';

export interface Model {
  createdAt: string;
  /** hoje, YYYY-MM-DD — as telas comparam contra isto em vez de criar Date solto */
  today: string;
  accounts: AccountRow[];
  archivedAccounts: AccountRow[];
  cards: CreditCardRow[];
  invoices: InvoiceRow[];
  categories: Map<string, CategoryRow>;
  subcategories: Map<string, SubcategoryRow>;
  tags: Map<string, TagRow>;
  /** séries recorrentes vivas (não apagadas), ativas e pausadas */
  recurring: RecurringRow[];
  /** recurringId → tags da série */
  recurringTags: Map<string, TagRow[]>;
  /** transactionId → tags do lançamento */
  transactionTags: Map<string, TagRow[]>;
  /** guardadas mas fora da análise: ainda movem o saldo da conta */
  ignored: TransactionRow[];
  /** caixinhas ativas */
  reservas: ReservaRow[];
  /** caixinhas arquivadas (o valor fica congelado, fora do Disponível) */
  archivedReservas: ReservaRow[];
  /** guardar/resgatar/rendimento de todas as caixinhas, data desc */
  reservaMovements: ReservaMovementRow[];
  /** metas não excluídas, inclusive as concluídas */
  goals: GoalRow[];
  /** depósitos das metas, data desc */
  goalDeposits: GoalDepositRow[];
  /** Financiamentos e Empréstimos: contratos (todos os status) e abatimentos */
  financings: FinancingRow[];
  financingAdjustments: FinancingAdjustmentRow[];
  /** Quem me deve: pessoas, valores a receber (abertos e encerrados) e o razão de eventos */
  people: Map<string, PersonRow>;
  personalLoans: PersonalLoanRow[];
  personalLoanEvents: PersonalLoanEventRow[];
  /** orçamentos ativos, já com as categorias e os recortes resolvidos */
  budgets: BudgetView[];
  /** ordenadas por data desc; inclui as ocorrências projetadas das recorrências */
  transactions: TransactionRow[];
  /** primeiro e último mês navegáveis (o último = o maior entre hoje, o último lançamento confirmado,
   *  a última recorrência projetada e o vencimento da última fatura com compra) */
  firstMonth: string;
  lastMonth: string;
  latestMonth: string;
}

/** Até onde a projeção de recorrência vai. Um ano cobre o "vou pagar isso o ano todo?"
 * sem encher a navegação de meses vazios. */
const PROJECTION_MONTHS = 12;

export interface BudgetView extends Pick<BudgetRow, 'id' | 'name' | 'type' | 'icon' | 'color' | 'amount' | 'period' | 'cycleStartDay' | 'cycleAnchorDate'> {
  /** categorias vigiadas (a legada `categoryId` mais as de budgetCategories) */
  categoryIds: string[];
  /** vazio = todas as origens */
  origins: Array<{ accountId: string | null; creditCardId: string | null }>;
  /** categoryId → subcategorias que contam; categoria fora do mapa conta inteira */
  subScope: Map<string, Set<string>>;
  limits: Array<{ subcategoryId: string; amount: number }>;
  /** dia em que o orçamento foi criado (yyyy-MM-dd; vazio quando o backup não traz) */
  createdOn: string;
}

function buildBudgets(d: BackupPayload['data'], subcategories: Map<string, SubcategoryRow>): BudgetView[] {
  const byBudget = <T extends { budgetId: string }>(rows: T[] | undefined) => {
    const m = new Map<string, T[]>();
    for (const r of rows ?? []) {
      const list = m.get(r.budgetId);
      if (list) list.push(r); else m.set(r.budgetId, [r]);
    }
    return m;
  };
  const cats = byBudget(d.budgetCategories);
  const origins = byBudget(d.budgetOrigins);
  const subs = byBudget(d.budgetSubcategories);
  const limits = byBudget(d.budgetSubcategoryLimits);
  return (d.budgets ?? [])
    .filter(b => b.isActive && !b.deletedAt)
    .map(b => {
      const categoryIds = [...new Set([...(b.categoryId ? [b.categoryId] : []), ...(cats.get(b.id) ?? []).map(c => c.categoryId)])];
      const subScope = new Map<string, Set<string>>();
      for (const row of subs.get(b.id) ?? []) {
        const sub = subcategories.get(row.subcategoryId);
        if (!sub) continue;
        const set = subScope.get(sub.categoryId) ?? new Set<string>();
        set.add(sub.id);
        subScope.set(sub.categoryId, set);
      }
      return {
        id: b.id, name: b.name, type: b.type, icon: b.icon, color: b.color, amount: b.amount, period: b.period,
        cycleStartDay: b.cycleStartDay ?? 1, cycleAnchorDate: b.cycleAnchorDate ?? null,
        categoryIds,
        origins: (origins.get(b.id) ?? []).map(o => ({ accountId: o.accountId ?? null, creditCardId: o.creditCardId ?? null })),
        subScope,
        limits: (limits.get(b.id) ?? []).map(l => ({ subcategoryId: l.subcategoryId, amount: l.amount })),
        createdOn: (b.createdAt ?? '').slice(0, 10),
      };
    });
}

function todayISO(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/** Regra de visibilidade do app: linha na lixeira (`deleted_at` preenchido) não existe pra
 * nenhuma tela. O backup já sai sem elas, mas pelo sync a exclusão chega como uma célula
 * `deleted_at` por cima da linha do snapshot — sem este filtro, o excluído continuava na tela. */
const alive = <T extends { deletedAt?: string | null }>(rows: T[] | undefined): T[] => (rows ?? []).filter(r => !r.deletedAt);

export function buildModel(payload: BackupPayload): Model {
  const d = payload.data;
  const allAccounts = alive(d.accounts).sort((a, b) => a.sortOrder - b.sortOrder);
  const accounts = allAccounts.filter(a => !a.isArchived);
  const archivedAccounts = allAccounts.filter(a => a.isArchived);
  const cards = alive(d.creditCards).filter(c => !c.isArchived).sort((a, b) => a.sortOrder - b.sortOrder);
  const categories = new Map(alive(d.categories).map(c => [c.id, c]));
  const subcategories = new Map(alive(d.subcategories).map(s => [s.id, s]));
  const tags = new Map((d.tags ?? []).map(t => [t.id, t]));
  const recurring = alive(d.recurringTransactions);
  const recurringTags = new Map<string, TagRow[]>();
  for (const link of d.recurringTransactionTags ?? []) {
    const tag = tags.get(link.tagId);
    if (!tag) continue;
    const list = recurringTags.get(link.recurringId);
    if (list) list.push(tag); else recurringTags.set(link.recurringId, [tag]);
  }

  const transactionTags = new Map<string, TagRow[]>();
  for (const link of d.transactionTags ?? []) {
    const tag = tags.get(link.tagId);
    if (!tag) continue;
    const list = transactionTags.get(link.transactionId);
    if (list) list.push(tag); else transactionTags.set(link.transactionId, [tag]);
  }

  const kept = alive(d.transactions);
  const stored = kept.filter(t => !t.isIgnored);
  const ignored = kept.filter(t => t.isIgnored);
  const reservas = alive(d.reservas).filter(r => !r.isArchived);
  const archivedReservas = alive(d.reservas).filter(r => r.isArchived);
  const byDateDesc = <T extends { date: string }>(rows: T[]) => [...rows].sort((a, b) => b.date.localeCompare(a.date));
  const reservaMovements = byDateDesc(d.reservaMovements ?? []);
  const goals = alive(d.goals);
  const goalDeposits = byDateDesc(d.goalDeposits ?? []);
  const financings = d.financings ?? [];
  const financingAdjustments = d.financingAdjustments ?? [];
  const people = new Map((d.people ?? []).map(p => [p.id, p]));
  const personalLoans = d.personalLoans ?? [];
  const personalLoanEvents = byDateDesc(d.personalLoanEvents ?? []);
  const budgets = buildBudgets(d, subcategories);
  const today = todayMonth();
  const day = todayISO();

  // A chave sai de TODAS as linhas guardadas, inclusive as ignoradas: a ocorrência existe
  // mesmo quando fica fora da análise, e projetar por cima dela dobraria o previsto.
  const projected = projectRecurring(
    recurring,
    `${today}-01`,
    `${addMonths(today, PROJECTION_MONTHS)}-31`,
    materializedKeys(kept),
  );

  const transactions = [...stored, ...projected]
    .sort((a, b) => (b.date + (b.time ?? '')).localeCompare(a.date + (a.time ?? '')));

  const confirmedMonths = stored.filter(t => t.isConfirmed).map(t => t.date.slice(0, 7));
  const allMonths = stored.map(t => t.date.slice(0, 7));
  const firstMonth = allMonths.length ? allMonths.reduce((a, b) => (a < b ? a : b)) : today;
  const lastConfirmed = confirmedMonths.length ? confirmedMonths.reduce((a, b) => (a > b ? a : b)) : today;
  const lastProjected = projected.length
    ? projected.map(t => t.date.slice(0, 7)).reduce((a, b) => (a > b ? a : b))
    : today;
  // A fatura é nomeada pelo mês do vencimento (cards.ts): a compra de hoje pode estar numa fatura
  // que vence no mês que vem, e a tela de Cartões só mostra essa fatura se a seta chegar lá.
  const cardIds = new Set(cards.map(c => c.id));
  const lastInvoice = (d.invoices ?? [])
    .filter(i => cardIds.has(i.creditCardId) && withCharges(i))
    .map(i => displayMonth(i))
    .reduce((a, b) => (a > b ? a : b), today);
  const lastMonth = [today, lastConfirmed, lastProjected, lastInvoice].reduce((a, b) => (a > b ? a : b));
  const latestMonth = today <= lastMonth && today >= firstMonth ? today : lastMonth;
  return {
    createdAt: payload.createdAt,
    today: day,
    accounts, archivedAccounts, cards,
    invoices: d.invoices ?? [],
    categories, subcategories, tags, recurring, recurringTags, transactionTags, ignored, reservas, archivedReservas, reservaMovements, goals, goalDeposits, financings, financingAdjustments, people, personalLoans, personalLoanEvents, budgets,
    transactions, firstMonth, lastMonth, latestMonth,
  };
}

export function totalBalance(accounts: AccountRow[]): number {
  return accounts.filter(a => !a.isExcludedFromTotals).reduce((sum, a) => sum + a.currentBalance, 0);
}

/**
 * Pagamento de fatura (feito ou previsto): sai da conta, mas NÃO é gasto — o gasto são as
 * compras no cartão. Somar os dois dobra a saída do mês. Mesma regra do app
 * (transactionRepository exclui INVOICE_PAYMENT/INVOICE_EXPENSE das somas).
 */
export function isInvoiceSettlement(t: TransactionRow): boolean {
  return t.adjustmentType === 'invoice_payment' || t.adjustmentType === 'invoice_expense';
}

/** Artefatos de fatura que não são gasto nem receita (transactionScopes.ts, NON_ANALYTIC_INVOICE_ADJUSTMENT_TYPES). */
const NON_ANALYTIC_ADJUSTMENTS = new Set(['invoice_credit', 'invoice_expense', 'invoice_manual_adjustment', 'invoice_manual_entry', 'invoice_payment', 'invoice_remainder']);
/** Principal de empréstimo a pessoa: dinheiro que sai e volta, não é despesa nem receita (excludePersonalLoanPrincipal). */
const LOAN_PRINCIPAL = new Set(['disbursement', 'scheduled_receipt', 'receipt']);

/** Conta como gasto/receita nos totais e nas categorias — o mesmo escopo analítico do app:
 * receita ou despesa, sem artefato de fatura e sem principal de empréstimo a pessoa
 * (juros e excedente entram). */
export function countsAsFlow(t: TransactionRow): boolean {
  if (t.type !== 'income' && t.type !== 'expense') return false;
  if (t.adjustmentType && NON_ANALYTIC_ADJUSTMENTS.has(t.adjustmentType)) return false;
  if (t.personalLoanEntryType && LOAN_PRINCIPAL.has(t.personalLoanEntryType)) return false;
  return true;
}

export interface Totals {
  income: number;
  expense: number;
  /** ainda não confirmados (previstos) */
  projectedIncome: number;
  projectedExpense: number;
}

/** Totais em COMPETÊNCIA, como a Home do app (accrualTotals): a compra no cartão não conta
 * na data da compra — quem conta é a FATURA que vence no período —, e o previsto (recorrência
 * projetada) já entra no número, em vez de ficar do lado. Confirmado contra o app com o
 * backup real: 5.218,91 de conta + 10.257,68 de faturas + 1.066,31 de recorrência projetada
 * = 16.542,90, o mesmo que o app mostra. */
export interface AccrualTotals {
  income: number;
  expense: number;
  /** parte de `expense` que veio das faturas do período */
  invoices: number;
  /** parte de `income` que ainda não foi confirmada */
  projectedIncome: number;
  /** parte de `expense` que ainda não foi confirmada (as faturas não entram aqui) */
  projectedExpense: number;
}

export function accrualTotals(transactions: TransactionRow[], invoices: InvoiceRow[], range: Range): AccrualTotals {
  const out: AccrualTotals = { income: 0, expense: 0, invoices: 0, projectedIncome: 0, projectedExpense: 0 };
  for (const t of transactions) {
    if (!inPeriod(t.date, range) || !countsAsFlow(t)) continue;
    // Compra no cartão fica de fora: ela entra pela fatura, senão contaria duas vezes.
    if (t.type === 'expense' && t.creditCardId) continue;
    if (t.type === 'income') {
      out.income += t.amount;
      if (!t.isConfirmed) out.projectedIncome += t.amount;
    } else {
      out.expense += t.amount;
      if (!t.isConfirmed) out.projectedExpense += t.amount;
    }
  }
  for (const i of invoices) {
    if (!inPeriod(i.dueDate, range)) continue;
    out.invoices += i.totalAmount;
    out.expense += i.totalAmount;
  }
  return out;
}

export interface MonthTotals extends Totals {
  month: string;
}

type Range = Pick<Period, 'from' | 'to'>;

export function totalsIn(transactions: TransactionRow[], range: Range): Totals {
  const out: Totals = { income: 0, expense: 0, projectedIncome: 0, projectedExpense: 0 };
  for (const t of transactions) {
    if (!inPeriod(t.date, range) || !countsAsFlow(t)) continue;
    if (t.type === 'income') {
      if (t.isConfirmed) out.income += t.amount; else out.projectedIncome += t.amount;
    } else if (t.type === 'expense') {
      if (t.isConfirmed) out.expense += t.amount; else out.projectedExpense += t.amount;
    }
  }
  return out;
}

function monthRange(ym: string): Range {
  return { from: `${ym}-01`, to: `${ym}-31` };
}

export function monthTotals(transactions: TransactionRow[], month: string): MonthTotals {
  return { month, ...totalsIn(transactions, monthRange(month)) };
}

/** Série mensal: os `count` meses que terminam em `endMonth`, ou os meses dados. */
export function monthlySeries(transactions: TransactionRow[], endMonth: string, count = 12): MonthTotals[] {
  const out: MonthTotals[] = [];
  for (let i = count - 1; i >= 0; i--) out.push(monthTotals(transactions, addMonths(endMonth, -i)));
  return out;
}

export function monthlySeriesOf(transactions: TransactionRow[], months: string[]): MonthTotals[] {
  return months.map(m => monthTotals(transactions, m));
}

export interface CategorySlice {
  id: string;
  name: string;
  color: string;
  total: number;
  count: number;
  /** total no período anterior, pra comparação */
  previous?: number;
}

export function categoryBreakdown(
  transactions: TransactionRow[],
  categories: Map<string, CategoryRow>,
  range: Range,
  type: 'expense' | 'income' = 'expense',
  previous?: Range,
  /** 'projected' olha o que ainda não caiu — o único conteúdo de um mês no futuro */
  source: 'confirmed' | 'projected' = 'confirmed',
): CategorySlice[] {
  const acc = new Map<string, CategorySlice>();
  for (const t of transactions) {
    if (t.type !== type || (source === 'projected') === !!t.isConfirmed || !countsAsFlow(t)) continue;
    const isCur = inPeriod(t.date, range);
    const isPrev = !!previous && inPeriod(t.date, previous);
    if (!isCur && !isPrev) continue;
    const cat = t.categoryId ? categories.get(t.categoryId) : undefined;
    const id = cat?.id ?? '__none__';
    const slice = acc.get(id) ?? { id, name: cat?.name ?? 'Sem categoria', color: cat?.color ?? '#9CA3AF', total: 0, count: 0, previous: 0 };
    if (isCur) { slice.total += t.amount; slice.count += 1; }
    else slice.previous = (slice.previous ?? 0) + t.amount;
    acc.set(id, slice);
  }
  return [...acc.values()].filter(s => s.total > 0 || (s.previous ?? 0) > 0).sort((a, b) => b.total - a.total);
}

export interface CardInvoiceView {
  card: CreditCardRow;
  invoice: InvoiceRow | null;
  /** soma das faturas ainda não pagas (limite comprometido) */
  committed: number;
}

export function cardInvoices(cards: CreditCardRow[], invoices: InvoiceRow[], month: string): CardInvoiceView[] {
  const horizon = addMonths(todayMonth(), 1);
  return cards.map(card => {
    const mine = invoices.filter(i => i.creditCardId === card.id);
    // ADR 0102 do app: a fatura é NOMEADA pelo mês do vencimento (referenceMonth é identidade interna).
    // Fatura vazia (pré-carga da tela do cartão) não conta como "a do mês" — ver withCharges em cards.ts.
    const invoice = mine.find(i => i.dueDate.slice(0, 7) === month && withCharges(i)) ?? null;
    const committed = mine
      .filter(i => i.status !== 'paid' && i.referenceMonth <= horizon)
      .reduce((sum, i) => sum + Math.max(i.totalAmount - i.paidAmount, 0), 0);
    return { card, invoice, committed };
  });
}

/** Faturas de um cartão, da mais recente pra mais antiga, sem as projeções distantes. */
export function invoicesOfCard(invoices: InvoiceRow[], cardId: string): InvoiceRow[] {
  const horizon = addMonths(todayMonth(), 2);
  return invoices
    .filter(i => i.creditCardId === cardId && i.referenceMonth <= horizon && (i.totalAmount > 0 || i.status === 'paid'))
    .sort((a, b) => b.referenceMonth.localeCompare(a.referenceMonth));
}

export function transactionsIn(transactions: TransactionRow[], range: Range): TransactionRow[] {
  return transactions.filter(t => inPeriod(t.date, range));
}

export function recentTransactions(transactions: TransactionRow[], limit: number): TransactionRow[] {
  const today = new Date().toISOString().slice(0, 10);
  return transactions.filter(t => t.isConfirmed && t.date <= today).slice(0, limit);
}
